import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Inspector deactivate dialog (AUT-125, architect guardrail 🟠 #3).
 *
 * A required `reason` (max 500, mirrors backend `SetInspectorStatusSchema`)
 * collected via RHF + Zod. When `activeOrdersCount > 0` an `<Alert
 * severity="warning">` surfaces ABOVE the reason field — the count uses
 * i18next pluralisation. When 0, the alert is omitted (no '0 active
 * orders' noise).
 *
 * Backend remains the 409 authority on the FSM transition — this dialog
 * is collecting the reason + showing the warning.
 */
const MAX_REASON = 500;

const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'inspectors.deactivateDialog.reasonRequired')
    .max(MAX_REASON, 'inspectors.deactivateDialog.reasonMax'),
});

type DeactivateForm = z.infer<typeof schema>;

export interface DeactivateInspectorDialogProps {
  open: boolean;
  activeOrdersCount: number;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const DeactivateInspectorDialog = ({
  open,
  activeOrdersCount,
  loading = false,
  onConfirm,
  onCancel,
}: DeactivateInspectorDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<DeactivateForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, reset]);

  const submit = ({ reason }: DeactivateForm): void => {
    if (loading) return;
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inspectors.deactivateDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>{t('inspectors.deactivateDialog.description')}</DialogContentText>
          {activeOrdersCount > 0 ? (
            <Alert severity="warning">
              {t('inspectors.deactivateDialog.activeOrdersWarning', { count: activeOrdersCount })}
            </Alert>
          ) : null}
          <TextField
            {...register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            label={t('inspectors.deactivateDialog.reasonLabel')}
            error={Boolean(errors.reason)}
            helperText={errors.reason ? t(errors.reason.message ?? '') : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || !isValid}
            loading={loading}
          >
            {t('inspectors.deactivateDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
